import { Brand } from 'API';
import { useDaysInLab } from 'hooks/use-days-in-lab';
import { usePrevious } from 'hooks/use-previous';
import _, { map } from 'lodash';
import moment from 'moment';
import { useManufacturingLocation } from 'providers/ManufacturingLocationProvider';
import {
  OrderModuleActionsContext,
  OrderModuleContext,
  TechnicalPreferencesByProductCode,
  useOrderEntryContext,
} from 'providers/OrderModuleProvider';
import { ShortcutKeysContext } from 'providers/ShortcutKeysProvider';
import { ToastContext } from 'providers/ToastProvider';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getTechnicalPreferences } from 'shared/api/technical-preferences.api';
import { LAB_HOLIDAY_DATES } from 'shared/constants/constants';
import { ShortCutKeys } from 'shared/constants/shortcut-keys.constants';
import { ToastNotificationType } from 'shared/enums';
import { getDefaultOrderItemInput } from 'shared/helpers/order-entry/order-entry.helper';
import { useLazyQueryFetcher } from 'shared/hooks/useLazyQueryFetcher';
import { LocalOrderItemInput } from 'shared/models';
import { addBusinessDaysSkippingHolidays } from 'shared/utils';
import { useLabStore } from 'stores/useLabStore';
import Product from '../Product/Product';
import { OverrideField } from '../ProductionFacility/types';
import TabStrip from '../TabStrip/TabStrip';

const isOverrideOrderItemDaysInLab = (orderItem: LocalOrderItemInput) => {
  return !!orderItem.overrides?.some(override => override.field === OverrideField.DaysInLab);
};

/**
 * Props for the Products component.
 */
interface ProductsProps {
  /**
   * Flag indicating whether classifications are loading.
   */
  isClassificationsLoading: boolean;
  /**
   * Flag indicating whether it's an order update.
   */
  isOrderUpdate?: boolean;
}

/**
 * Represents the list of products in the order.
 * @param isClassificationsLoading - Flag indicating whether classifications are loading.
 * @param isOrderUpdate - Flag indicating whether it's an order update. (optional)
 * @returns JSX element representing the Products component.
 */
const Products: React.FC<ProductsProps> = ({ isClassificationsLoading, isOrderUpdate }) => {
  const shortcutKeyCtx = useContext(ShortcutKeysContext);
  const { order, isDigitalOrder } = useContext(OrderModuleContext);
  const { patchOrder, setTechnicalPreferencesByProductCode } = useContext(OrderModuleActionsContext);
  const { selectedOrderItemId, setSelectedOrderItemId } = useOrderEntryContext();
  const prevProviderId = usePrevious(order.providerId);
  const toast = useContext(ToastContext);

  const { getManufacturingLocation } = useManufacturingLocation();
  const selectedLab = useLabStore(state => state.selectedLab);
  const { manufacturingFacilityTimeZoneId = moment.tz.guess() } =
    getManufacturingLocation(selectedLab.facilityId) ?? {};

  const [allProductInfoCached, setAllProductInfoCached] = useState<
    Record<
      string,
      {
        salesCategoryAndBackLogGroupAttributeRecord: Record<string, string[]>;
        salesCategoryAndBackLogGroupProductRecord: Record<string, string[]>;
        productBrands: Brand[];
      }
    >
  >({});

  const allProductBrandsResult = useMemo(() => {
    const result = Object.entries(allProductInfoCached).reduce((acc, [productCode, productInfo]) => {
      const productBrands = productInfo.productBrands;
      if (!productBrands) return acc;
      acc[productCode] = productBrands;
      return acc;
    }, {} as Record<string, Brand[]>);

    return result;
  }, [allProductInfoCached]);

  const { calculateTotalDaysInLab, calculateMaxDaysInLab, getProductLevelDaysInLab } =
    useDaysInLab(allProductBrandsResult);

  const { fetcher: preferencesByAccountAndProviderFetcher, loading: isPreferencesLoading } =
    useLazyQueryFetcher(getTechnicalPreferences);

  /**
   * Retrieves technical preferences using the provided billing account and provider IDs.
   */
  const loadTechnicalPreferences = useCallback(async () => {
    if (order.billingAccountId && order.providerId && order.providerId !== prevProviderId) {
      try {
        const preferencesByAccountAndProvider = await preferencesByAccountAndProviderFetcher({
          input: {
            billingAccountId: order.billingAccountId,
            providerId: order.providerId,
          },
        });
        const technicalPreferencesByProductCode: TechnicalPreferencesByProductCode = {};
        preferencesByAccountAndProvider.preferences.forEach(preference => {
          const { productCode } = preference;
          // Initializes the array of preferences for this product code.
          if (!technicalPreferencesByProductCode[productCode]) {
            technicalPreferencesByProductCode[productCode] = [];
          }
          technicalPreferencesByProductCode[productCode].push(preference);
        });
        setTechnicalPreferencesByProductCode(technicalPreferencesByProductCode);
      } catch (err) {
        const error = err as Error;
        if (error.name !== 'NotFoundError') {
          // Resets technical preferences on error upon receiving an error condition.
          // That way, existing preferences don't carry over to a new billing account or provider selection.
          setTechnicalPreferencesByProductCode({});
          toast.notify('Error loading technical preferences.', ToastNotificationType.Error);
        }
      }
    }
  }, [
    order.billingAccountId,
    order.providerId,
    preferencesByAccountAndProviderFetcher,
    prevProviderId,
    setTechnicalPreferencesByProductCode,
    toast,
  ]);

  useEffect(() => {
    loadTechnicalPreferences();
  }, [loadTechnicalPreferences]);
  /**
   * Add product to order items, update order context, and set as selected product.
   */
  const addProductHandler = useCallback(() => {
    const newOrder = _.cloneDeep(order);
    const newItem = getDefaultOrderItemInput();
    newOrder.orderItems.push(newItem);

    patchOrder({
      orderItems: newOrder.orderItems,
    });

    setSelectedOrderItemId(newItem.id);
  }, [order, patchOrder, setSelectedOrderItemId]);

  useEffect(() => {
    if (shortcutKeyCtx.keyPressed === ShortCutKeys.AltP) {
      addProductHandler();
      shortcutKeyCtx.reset();
    }
  }, [addProductHandler, shortcutKeyCtx]);

  useEffect(() => {
    // If there are no order items, add one.
    if (!order.orderItems.length) {
      addProductHandler();
    }

    // If there is no selected product or no matching order item with selected product id, set to first in array.
    if (
      !!order.orderItems.length &&
      (!selectedOrderItemId || !order.orderItems.some(item => item.id === selectedOrderItemId))
    ) {
      setSelectedOrderItemId(order.orderItems[0].id);
    }
  }, [order.orderItems, order.orderItems.length, selectedOrderItemId, addProductHandler, setSelectedOrderItemId]);

  /**
   * Remove a product from the tab list
   * @param id - removing item id
   */
  const removeProductHandler = (id: string) => {
    const productCount = order.orderItems.length;
    const newOrder = _.cloneDeep(order);
    const foundOrderItemIndex = newOrder.orderItems.findIndex(item => item.id === id);
    const currSelectedIndex = newOrder.orderItems.findIndex(item => item.id === selectedOrderItemId);

    if (foundOrderItemIndex > -1) {
      // If there is only one item, set current one to default and update selected to new order item id.
      if (newOrder.orderItems.length === 1) {
        const newOrderItemDefault = getDefaultOrderItemInput();
        newOrder.orderItems[foundOrderItemIndex] = newOrderItemDefault;
      } else {
        newOrder.orderItems.splice(foundOrderItemIndex, 1);
        // set a new selection if the user is removing the product they're currently on
        // or to keep the selection on the same item when removing an element in the list
        if (
          (id === selectedOrderItemId && foundOrderItemIndex === productCount - 1) ||
          currSelectedIndex > foundOrderItemIndex
        ) {
          setSelectedOrderItemId(newOrder.orderItems[currSelectedIndex - 1].id);
        } else {
          setSelectedOrderItemId(newOrder.orderItems[currSelectedIndex].id);
        }
      }

      patchOrder({
        orderItems: newOrder.orderItems,
      });
    }
  };

  /**
   * Calculate the total days in lab for the order.
   */
  const orderLevelDaysInLab = useMemo(() => {
    let totalDaysInLab = 0;
    let attributeLevelDaysInLab = 0;
    let productLevelDaysInLab = 0;

    let orderItems = [...order.orderItems];

    const combinedAllAttributeLevelDaysInLab = Object.entries(allProductInfoCached).reduce<
      Record<string, Record<string, Set<string>>>
    >((acc, [productCode, productInfo]) => {
      const salesCategoryAndBackLogGroupAttributeRecord = productInfo.salesCategoryAndBackLogGroupAttributeRecord;
      Object.entries(salesCategoryAndBackLogGroupAttributeRecord).forEach(([key, attributeNames]) => {
        if (!attributeNames.length) return; // skip if there are no attribute names

        // Filter order items by product code and attribute names.
        const filteredOrderItems = orderItems.filter(item => item.productCode === productCode);
        if (!filteredOrderItems.length) return acc;

        if (!acc[productCode]) {
          acc[productCode] = {};
        }

        if (!acc[productCode][key]) {
          acc[productCode][key] = new Set<string>();
        }

        /**
         * Iterate through the filtered order items and add the attribute names to the set.
         * This is used to combine the attribute names for each product code and sales category/backlog group.
         * This way, we can avoid duplicates and only keep unique attribute names.
         */
        filteredOrderItems.forEach(orderItem => {
          orderItem.attributes.forEach(attr => {
            if (attr.name && attributeNames.includes(attr.name)) {
              acc[productCode][key].add(attr.name);
            }
          });
        });
      });
      return acc;
    }, {});

    // Iterate through the combined attribute level days in lab and calculate the attribute level days in lab.
    Object.entries(combinedAllAttributeLevelDaysInLab).forEach(([productCode, attributeRecord]) => {
      const filteredOrderItems = orderItems.filter(item => item.productCode === productCode);
      if (!filteredOrderItems.length) return;

      const filteredOverrideOrderItems: LocalOrderItemInput[] = [];
      const filteredNonOverrideOrderItems: LocalOrderItemInput[] = [];

      filteredOrderItems.forEach(orderItem => {
        if (isOverrideOrderItemDaysInLab(orderItem)) {
          // If the order item has overridden days in lab, add it to the filtered override order items.
          filteredOverrideOrderItems.push(orderItem);
        } else {
          // If the order item does not have overridden days in lab, add it to the filtered non-override order items.
          filteredNonOverrideOrderItems.push(orderItem);
        }
      });

      // Iterate through the filtered override order items and calculate the attribute level days in lab.
      filteredOverrideOrderItems.forEach(orderItem => {
        // If the order item has overridden days in lab, add it to the attribute level days in lab.
        attributeLevelDaysInLab += orderItem.daysInLab ?? 0;
      });

      // Iterate through the filtered non-override order items and calculate the attribute level days in lab.
      filteredNonOverrideOrderItems.forEach(orderItem => {
        const addons = orderItem.addOns;
        const productionFacility = orderItem.manufacturingLocation;

        // calculate product level days in lab for add-ons/services
        const totalAddonDaysInLab =
          calculateTotalDaysInLab({
            attributeNames: map(addons, 'name'),
            productionFacility,
            isDigitalOrder,
            productCode,
          }) || 0;

        attributeLevelDaysInLab += totalAddonDaysInLab;

        Object.values(attributeRecord).forEach(attributeNames => {
          // calculate the max days in lab for each attribute
          // and sum them up to get the total max days in lab
          attributeLevelDaysInLab +=
            calculateMaxDaysInLab({
              attributeNames: Array.from(attributeNames),
              productionFacility,
              isDigitalOrder,
              productCode,
            }) || 0;
        });

        // convert set to array of attribute names
        // This is used to filter out the non-sales category and backlog group attributes.
        const salesCategoryAndBackLogGroupAttributeNames = Object.values(attributeRecord).flatMap(attrSet =>
          Array.from(attrSet)
        );

        // Filter out the non-sales category and backlog group attributes from the order item attributes.
        const filteredNonSalesCategoryAndBackLogGroupAttributes = orderItem.attributes.filter(
          attr => !salesCategoryAndBackLogGroupAttributeNames.includes(attr.name)
        );

        // calculate non-sales category and backlog group attributes days in lab
        attributeLevelDaysInLab +=
          calculateTotalDaysInLab({
            attributeNames: map(filteredNonSalesCategoryAndBackLogGroupAttributes, 'name'),
            productionFacility,
            isDigitalOrder,
            productCode,
          }) || 0; // Add the total days in lab for this attribute.
      });

      // Remove the product code from the order items array to avoid calculating the same product code again.
      orderItems = orderItems.filter(item => item.productCode !== productCode);
    });

    // Combine all product level days in lab into a single object.
    // This is used to calculate the product level days in lab.
    const combinedAllProductLevelDaysInLab = Object.entries(allProductInfoCached).reduce<Record<string, string[]>>(
      (acc, [, productInfo]) => {
        const salesCategoryAndBackLogGroupProductRecord = productInfo.salesCategoryAndBackLogGroupProductRecord;
        Object.entries(salesCategoryAndBackLogGroupProductRecord).forEach(([key, productCodes]) => {
          acc[key] = acc[key] || [];
          acc[key].push(...productCodes);
        });
        return acc;
      },
      {}
    );

    // Iterate through the combined product level days in lab and calculate the product level days in lab.
    Object.values(combinedAllProductLevelDaysInLab).forEach(productCodes => {
      const productLevelDaysInLabNumbers: number[] = [];

      productCodes.forEach(productCode => {
        const filteredOrderItems = orderItems.filter(item => item.productCode === productCode);

        if (!filteredOrderItems.length) return; // Skip if there are no order items for this product code.

        // Initialize the filtered override and non-override order items arrays.
        const filteredOverrideOrderItems: LocalOrderItemInput[] = [];
        const filteredNonOverrideOrderItems: LocalOrderItemInput[] = [];

        filteredOrderItems.forEach(orderItem => {
          if (isOverrideOrderItemDaysInLab(orderItem)) {
            // If the order item has overridden days in lab, add it to the filtered override order items.
            filteredOverrideOrderItems.push(orderItem);
          } else {
            // If the order item does not have overridden days in lab, add it to the filtered non-override order items.
            filteredNonOverrideOrderItems.push(orderItem);
          }
        });

        // Iterate through the filtered override order items and calculate the product level days in lab.
        filteredOverrideOrderItems.forEach(orderItem => {
          // If the order item has overridden days in lab, add it to the product level days in lab.
          productLevelDaysInLab += orderItem.daysInLab || 0;
        });

        // Iterate through the filtered non-override order items and calculate the product level days in lab.
        filteredNonOverrideOrderItems.forEach(orderItem => {
          const addons = orderItem.addOns;
          const productionFacility = orderItem.manufacturingLocation;

          // calculate product level days in lab for add-ons/services
          const totalAddonDaysInLab =
            calculateTotalDaysInLab({
              attributeNames: map(addons, 'name'),
              productionFacility,
              isDigitalOrder,
              productCode,
            }) || 0;

          const productLevelDaysInLab = getProductLevelDaysInLab({ isDigitalOrder, productCode, productionFacility });
          const totalSumOfProductLevelDaysInLab = productLevelDaysInLab + totalAddonDaysInLab; // add add-on days in lab to the product level days in lab
          productLevelDaysInLabNumbers.push(totalSumOfProductLevelDaysInLab); // Add the total days in lab for this product code to the array.
        });
      });

      // Filter out order items that are not in the product codes. meaning they productCodes already calculated
      // and remove them from the order items array. By doing this, we can avoid calculating the same product codes again.
      orderItems = orderItems.filter(item => !productCodes.includes(item.productCode));

      if (productLevelDaysInLabNumbers.length) {
        // Get the maximum days in lab for this product code.
        productLevelDaysInLab += Math.max(...productLevelDaysInLabNumbers);
      }
    });

    orderItems.forEach(orderItem => {
      const isDaysInLabOverridden = orderItem.overrides?.some(override => override.field === OverrideField.DaysInLab);
      // If the order item has overridden days in lab, add it to the total days in lab.
      // This is used to check if the order item has overridden days in lab.
      if (isDaysInLabOverridden) {
        totalDaysInLab += orderItem.daysInLab ?? 0;
        return;
      }
      // If the order item does not have overridden days in lab, calculate the days in lab based on sales category and backlog group attributes.
      const productCode = orderItem.productCode;
      const productionFacility = orderItem.manufacturingLocation;

      // Calculate the total days in lab for the production facility.
      // If the current total days in lab is null, calculate the days in lab for each product code.
      const totalDaysInLabForFacility =
        calculateTotalDaysInLab({
          attributeNames: map(orderItem.attributes, 'name'),
          productionFacility,
          isDigitalOrder,
          productCode,
          returnNullIfExist: true,
        }) ?? getProductLevelDaysInLab({ isDigitalOrder, productCode, productionFacility });
      totalDaysInLab += totalDaysInLabForFacility; // Add the total days in lab for this attribute.

      // calculate total days in lab for add-ons/services
      const totalAddonDaysInLab = calculateTotalDaysInLab({
        attributeNames: map(orderItem.addOns, 'name'),
        productionFacility,
        isDigitalOrder,
        productCode,
      });

      // add the total days in lab for add-ons/services to the total days in lab
      if (totalAddonDaysInLab) {
        totalDaysInLab += totalAddonDaysInLab;
      }
    });

    // Return the sum of the attribute level days in lab and product level days in lab and the total days in lab.
    return attributeLevelDaysInLab + productLevelDaysInLab + totalDaysInLab;
  }, [
    order.orderItems,
    allProductInfoCached,
    calculateMaxDaysInLab,
    isDigitalOrder,
    calculateTotalDaysInLab,
    getProductLevelDaysInLab,
  ]);

  /**
   * Updates the estimated ship date for the order based on the total days in lab.
   * This effect runs whenever the order level days in lab changes.
   * It calculates the estimated ship date by adding the order level days in lab to the
   * lab received date, for existing orders, or to the current date, for new orders.
   * Please see LMS1-9021 for more information about the expected order start date.
   * This calculation skips holidays defined in LAB_HOLIDAY_DATES.
   * The estimated ship date is then updated in the order.
   */
  useEffect(() => {
    const labReceivedDate = order.labReceivedDate;
    const orderStartDate = labReceivedDate
      ? moment.tz(labReceivedDate, manufacturingFacilityTimeZoneId)
      : moment.tz(manufacturingFacilityTimeZoneId);
    const estimatedShipMoment = addBusinessDaysSkippingHolidays(orderStartDate, orderLevelDaysInLab, LAB_HOLIDAY_DATES);

    // Formats to the following format: 2025-05-09T09:31:56.575-07:00
    const estimatedShipDate = estimatedShipMoment.tz(manufacturingFacilityTimeZoneId).toISOString(true);

    // Update the order with the new estimated ship date.
    patchOrder({ estimatedShipDate });
  }, [orderLevelDaysInLab, patchOrder, manufacturingFacilityTimeZoneId, order.labReceivedDate]);

  return (
    <div className="border-t bg-white flex flex-col flex-1 overflow-auto">
      <TabStrip
        selectedProductId={selectedOrderItemId}
        onTabSelect={(id: string) => {
          setSelectedOrderItemId(id);
        }}
        onTabAdd={addProductHandler}
        onTabRemove={removeProductHandler}
      />
      <div className="bg-gray-50 flex-1 overflow-auto pb-20">
        {order.orderItems.map(orderItem => {
          return (
            <Product
              key={orderItem.id}
              selectedProductId={selectedOrderItemId}
              orderItem={orderItem}
              billingAccountId={order.billingAccountId}
              onProductRemove={() => removeProductHandler(orderItem.id)}
              isOrderUpdate={isOrderUpdate}
              isLoadingProductInformation={isPreferencesLoading || isClassificationsLoading}
              setAllProductInfoCached={setAllProductInfoCached}
            />
          );
        })}
      </div>
    </div>
  );
};

export default Products;
